import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import SeatMapView from "@/components/site/SeatMapView";
import FlightStepper from "@/components/site/FlightStepper";
import { draftStore, computeTotal } from "@/lib/flightDraft";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";
import { api } from "@/lib/api";
import { ArrowLeft, ArrowRight, Info, RefreshCw, Armchair, BaggageClaim, Utensils } from "lucide-react";
import { formatCheckedBaggage } from "@/lib/bagFormat";

const safeString = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try { return JSON.stringify(v); } catch { return ""; }
};

// Standard IATA SSR special-meal codes — always offered as a free preference
// (sent to the carrier as a Special Service Request, no charge).
const SSR_MEAL_OPTIONS = [
  { id: "VGML", name: "Vegetarian (VGML)", price: 0 },
  { id: "AVML", name: "Asian Vegetarian (AVML)", price: 0 },
  { id: "HNML", name: "Hindu Meal (HNML)", price: 0 },
  { id: "MOML", name: "Muslim / Halal Meal (MOML)", price: 0 },
  { id: "KSML", name: "Kosher Meal (KSML)", price: 0 },
  { id: "GFML", name: "Gluten-Free Meal (GFML)", price: 0 },
  { id: "DBML", name: "Diabetic Meal (DBML)", price: 0 },
  { id: "LSML", name: "Low-Salt Meal (LSML)", price: 0 },
  { id: "CHML", name: "Child Meal (CHML)", price: 0 },
  { id: "BBML", name: "Baby Meal (BBML)", price: 0 },
  { id: "SFML", name: "Seafood Meal (SFML)", price: 0 },
  { id: "FPML", name: "Fruit Platter (FPML)", price: 0 },
];

export default function FlightAncillaries() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const { format: fmt, currency: dispCcy } = useCurrency();
  const [draft, setDraft] = useState(null);
  const [anc, setAnc] = useState({ seats: {}, bags: {}, meals: {}, insurance: null });
  // ancillaries fetched live per segment from Amadeus pricing
  // shape: { [segIdx]: { loading, available, extra_baggage, meals, reason } }
  const [liveAnc, setLiveAnc] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/"); return; }
    const d = draftStore.get();
    if (!d || !d.selections?.length || !d.travelers?.length) { navigate("/flights"); return; }
    setDraft(d);
    setAnc(d.ancillaries || { seats: {}, bags: {}, meals: {}, insurance: null });
  }, [user, authLoading, navigate]);

  // Fetch real ancillaries per segment (Amadeus or Travelport, based on supplier_source)
  // AND probe seatmap availability up-front so we can show "not published" instead
  // of a misleading "Pick seat from cabin map" button when the carrier returns
  // a SOAP Fault `Seat Map is unavailable` (e.g. Saudi Arabian / Nile Air / Nesma
  // via Travelport 1G).
  useEffect(() => {
    if (!draft) return;
    let alive = true;
    (async () => {
      const next = {};
      for (let i = 0; i < draft.selections.length; i++) {
        next[i] = { loading: true };
      }
      if (alive) setLiveAnc(next);
      for (let i = 0; i < draft.selections.length; i++) {
        const sel = draft.selections[i];
        const isTP = (sel.flight.supplier_source || sel.flight.source) === "travelport";
        try {
          const url = isTP ? "/travelport/ancillaries" : "/flights/ancillaries";
          const body = isTP
            ? { flight: sel.flight, pax_count: Number(draft.search?.passengers || 1) }
            : { flight_offer: sel.flight.amadeus_offer };
          // Fan-out ancillaries + seatmap probe in parallel — keeps the loader
          // fast even when one of the two endpoints stalls.
          const [ancRes, smRes] = await Promise.all([
            api.post(url, body),
            isTP
              ? api.post("/travelport/seatmap", body).catch(() => ({ data: { available: false, reason: "Seat map unavailable" } }))
              : api.post("/flights/seatmap", { flight_offer: sel.flight.amadeus_offer }).catch(() => ({ data: { data: [] } })),
          ]);
          if (!alive) return;
          // For Amadeus, /flights/seatmap returns {data: [...]} — treat non-empty
          // as available. For Travelport, the endpoint already returns `available`.
          const smData = smRes.data || {};
          const smAvailable = (smData.available !== undefined)
            ? !!smData.available
            : Array.isArray(smData.data) && smData.data.length > 0;
          setLiveAnc((cur) => ({
            ...cur,
            [i]: { ...ancRes.data, loading: false,
                   seatmap_available: smAvailable,
                   seatmap_reason: smData.reason || null },
          }));
        } catch (_e) {
          if (!alive) return;
          setLiveAnc((cur) => ({ ...cur, [i]: { loading: false, available: false,
                                                seatmap_available: false,
                                                reason: "Pricing unavailable" } }));
        }
      }
    })();
    return () => { alive = false; };
  }, [draft]);

  const pax = useMemo(() => Number(draft?.search?.passengers || 1), [draft]);
  const [seatMapOpen, setSeatMapOpen] = useState(null);
  const segmentKey = (segIdx, paxIdx) => `${segIdx}-${paxIdx}`;

  if (!draft) return null;

  const setBag = (segIdx, paxIdx, bagItem) => {
    const k = segmentKey(segIdx, paxIdx);
    const next = { ...(anc.bags || {}) };
    if (!bagItem) delete next[k];
    else next[k] = {
      id: safeString(bagItem.id),
      name: safeString(bagItem.name),
      kg: bagItem.weight_kg || 0,
      qty: bagItem.quantity || 1,
      totalPrice: Number(bagItem.price || 0),
    };
    setAnc({ ...anc, bags: next });
  };

  const setMeal = (segIdx, paxIdx, mealItem) => {
    const k = segmentKey(segIdx, paxIdx);
    const next = { ...(anc.meals || {}) };
    if (!mealItem) delete next[k];
    else next[k] = {
      id: safeString(mealItem.id),
      name: safeString(mealItem.name),
      totalPrice: Number(mealItem.price || 0),
    };
    setAnc({ ...anc, meals: next });
  };

  const toggleInsurance = (val) => {
    if (val) setAnc({ ...anc, insurance: draft.selections[0].flight.ancillaries.insurance });
    else setAnc({ ...anc, insurance: null });
  };

  const submit = () => {
    draftStore.patch({ ancillaries: anc });
    navigate("/flights/review");
  };

  const total = computeTotal({ ...draft, ancillaries: anc });

  const NotPublished = ({ label, carrier }) => (
    <div className="mt-2 text-[11px] text-slate-500 italic flex items-start gap-1">
      <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
      <span>{label} not published by {carrier || "carrier"} — request at airport check-in.</span>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 py-8">
      <div className="bg-gradient-to-br from-[var(--brand-accent)] to-[var(--brand-primary-dark)] rounded-3xl p-6 md:p-8 flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-white font-heading text-2xl sm:text-3xl font-semibold">Add-ons & ancillaries</h1>
        <FlightStepper current="ancillaries" />
      </div>

      <div className="mt-6 space-y-5">
        {draft.selections.map((sel, segIdx) => {
          const a = sel.flight.ancillaries || {};
          const live = liveAnc[segIdx] || { loading: true };
          const bagItems = live.extra_baggage?.items || [];
          const mealItems = live.meals?.options || [];
          return (
            <Card key={segIdx} className="p-6" data-testid={`anc-segment-${segIdx}`}>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <div className="text-xs uppercase tracking-wider text-slate-400 font-bold">Segment {segIdx + 1}</div>
                  <div className="font-heading font-semibold text-[var(--brand-accent)]">
                    {safeString(sel.flight.airline_name)} · <span className="font-mono">{safeString(sel.flight.from)} → {safeString(sel.flight.to)}</span> · {safeString(sel.flight.depart_time)}
                  </div>
                </div>
                <div className="text-xs text-slate-500 flex items-center gap-2">
                  <span>{safeString(sel.fare.name)} fare</span>
                  {live.loading && <span className="inline-flex items-center gap-1 text-slate-400"><RefreshCw className="w-3 h-3 animate-spin" /> Loading ancillaries…</span>}
                  {!live.loading && live.source && <span className="text-[10px] bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full uppercase tracking-wider">Live · {safeString(live.source)}</span>}
                </div>
              </div>

              <div className="mt-4 space-y-4">
                {Array.from({ length: pax }).map((_, paxIdx) => {
                  const k = segmentKey(segIdx, paxIdx);
                  const trav = draft.travelers?.[paxIdx];
                  return (
                    <div key={paxIdx} className="border border-slate-200 rounded-xl p-4">
                      <div className="font-medium text-[var(--brand-accent)] mb-3">
                        Passenger {paxIdx + 1}{trav ? ` — ${safeString(trav.first_name)} ${safeString(trav.last_name)}` : ""}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                        {/* SEATS — Amadeus SeatMap / Travelport SeatMap */}
                        <div className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-teal-50/40 hover:shadow-md transition-shadow flex flex-col" data-testid={`anc-seat-cell-${segIdx}-${paxIdx}`}>
                          <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-[var(--brand-accent)]">
                            <span className="w-8 h-8 rounded-lg bg-teal-100 text-[var(--brand-primary-dark)] flex items-center justify-center flex-shrink-0">
                              <Armchair className="w-4 h-4" />
                            </span>
                            Seat
                          </div>
                          <div className="mt-auto">
                          {live.loading ? (
                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 h-9"><RefreshCw className="w-3 h-3 animate-spin" /> Loading…</div>
                          ) : live.seatmap_available === false ? (
                            <>
                              <Button data-testid={`anc-seat-pick-${segIdx}-${paxIdx}`} variant="outline" disabled className="w-full rounded-md text-xs h-9 justify-center font-medium cursor-not-allowed opacity-60">
                                Not available
                              </Button>
                              <div className="text-[10px] text-slate-500 italic flex items-start gap-1 mt-2">
                                <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                <span>{safeString(sel.flight.airline_name) || "This carrier"} hasn&apos;t published a seat map for this fare — seat will be assigned at check-in.</span>
                              </div>
                            </>
                          ) : (
                            <>
                              <Button data-testid={`anc-seat-pick-${segIdx}-${paxIdx}`} variant="outline" onClick={() => setSeatMapOpen({ segIdx, paxIdx })} className="w-full rounded-md text-xs h-9 justify-center font-medium">
                                {anc.seats?.[k]?.seat_number ? `Seat ${safeString(anc.seats[k].seat_number)}${anc.seats[k]?.price > 0 ? ` · +${fmt(anc.seats[k].price)}` : ""}` : "Pick seat from cabin map"}
                              </Button>
                              {!anc.seats?.[k]?.seat_number && (
                                <div className="text-[10px] text-slate-400 italic flex items-start gap-1 mt-2">
                                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>Choose your preferred seat from the cabin map.</span>
                                </div>
                              )}
                            </>
                          )}
                          </div>
                        </div>

                        {/* BAGGAGE — Amadeus pricing live */}
                        <div className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-amber-50/40 hover:shadow-md transition-shadow flex flex-col" data-testid={`anc-bags-cell-${segIdx}-${paxIdx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-accent)]">
                              <span className="w-8 h-8 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
                                <BaggageClaim className="w-4 h-4" />
                              </span>
                              Extra baggage
                            </div>
                            {bagItems.length > 0 && <span className="text-[10px] text-amber-800 bg-amber-100 px-2 py-0.5 rounded-full font-medium">{bagItems.length}</span>}
                          </div>
                          <div className="mt-auto">
                          {live.loading ? (
                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1 h-9"><RefreshCw className="w-3 h-3 animate-spin" /> Loading…</div>
                          ) : bagItems.length === 0 ? (
                            <NotPublished label="Extra baggage" carrier={safeString(sel.flight.airline_name)} />
                          ) : (
                            <Select
                              value={anc.bags?.[k]?.id || "none"}
                              onValueChange={(v) => {
                                if (v === "none") setBag(segIdx, paxIdx, null);
                                else setBag(segIdx, paxIdx, bagItems.find((b) => String(b.id) === v));
                              }}
                            >
                              <SelectTrigger data-testid={`anc-bag-${segIdx}-${paxIdx}`} className="h-9 text-xs">
                                <SelectValue placeholder="Add a bag" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none">No extra bag</SelectItem>
                                {bagItems.map((b) => (
                                  <SelectItem key={b.id} value={String(b.id)}>
                                    {safeString(b.name)} · {formatCheckedBaggage(b.quantity, b.weight_kg)} · +{fmt(Number(b.price))}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          )}
                          </div>
                        </div>

                        {/* MEALS — Paid meals (when published) + free SSR meal preferences */}
                        <div className="border border-slate-200 rounded-xl p-4 bg-gradient-to-br from-white to-rose-50/40 hover:shadow-md transition-shadow" data-testid={`anc-meals-cell-${segIdx}-${paxIdx}`}>
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2 text-sm font-semibold text-[var(--brand-accent)]">
                              <span className="w-8 h-8 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center">
                                <Utensils className="w-4 h-4" />
                              </span>
                              Meal
                            </div>
                            {mealItems.length > 0 && <span className="text-[10px] text-rose-800 bg-rose-100 px-2 py-0.5 rounded-full font-medium">{mealItems.length} paid</span>}
                          </div>
                          {live.loading ? (
                            <div className="text-[11px] text-slate-400 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3 animate-spin" /> Loading…</div>
                          ) : (
                            <>
                              <Select
                                value={anc.meals?.[k]?.id || "none"}
                                onValueChange={(v) => {
                                  if (v === "none") setMeal(segIdx, paxIdx, null);
                                  else {
                                    const all = [...mealItems, ...SSR_MEAL_OPTIONS];
                                    setMeal(segIdx, paxIdx, all.find((m) => String(m.id) === v));
                                  }
                                }}
                              >
                                <SelectTrigger data-testid={`anc-meal-${segIdx}-${paxIdx}`} className="h-9 text-xs">
                                  <SelectValue placeholder="Choose a meal" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No special meal</SelectItem>
                                  {mealItems.length > 0 && mealItems.map((m) => (
                                    <SelectItem key={`paid-${m.id}`} value={String(m.id)}>
                                      {safeString(m.name)} · +{fmt(Number(m.price))}
                                    </SelectItem>
                                  ))}
                                  {SSR_MEAL_OPTIONS.map((m) => (
                                    <SelectItem key={`ssr-${m.id}`} value={String(m.id)}>
                                      {safeString(m.name)} · Free
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              {mealItems.length === 0 && (
                                <div className="mt-2 text-[10px] text-slate-400 italic flex items-start gap-1">
                                  <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                  <span>Free preference — requested with carrier as SSR.</span>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          );
        })}

        {/* INSURANCE — our own product, always shown */}
        <Card className="p-6" data-testid="anc-insurance-card">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-teal-100 to-teal-200 text-[var(--brand-primary-dark)] flex items-center justify-center shadow-sm">
                <i className="fa-solid fa-shield-heart text-lg" aria-hidden="true"></i>
              </div>
              <div>
                <div className="font-heading font-semibold text-[var(--brand-accent)]">Trip Protect Insurance</div>
                <div className="text-xs text-slate-500 max-w-md">{safeString(draft.selections[0].flight.ancillaries?.insurance?.description)}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="font-heading font-bold text-[var(--brand-accent)]">{fmt(Number(draft.selections[0].flight.ancillaries?.insurance?.price || 0))}</div>
                <div className="text-xs text-slate-500">per pax</div>
              </div>
              <Switch data-testid="anc-insurance-toggle" checked={!!anc.insurance} onCheckedChange={toggleInsurance} />
            </div>
          </div>
        </Card>
      </div>

      {/* Seat map dialog */}
      <Dialog open={!!seatMapOpen} onOpenChange={(o) => !o && setSeatMapOpen(null)}>
        {seatMapOpen && (
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl text-[var(--brand-accent)]">
                Choose your seat — Segment {seatMapOpen.segIdx + 1} · Passenger {seatMapOpen.paxIdx + 1}
              </DialogTitle>
            </DialogHeader>
            <SeatMapView
              supplier={(draft.selections[seatMapOpen.segIdx].flight.supplier_source || draft.selections[seatMapOpen.segIdx].flight.source) === "travelport" ? "travelport" : "amadeus"}
              flightOffer={draft.selections[seatMapOpen.segIdx].flight.amadeus_offer}
              travelportFlight={draft.selections[seatMapOpen.segIdx].flight}
              segmentIndex={seatMapOpen.segIdx}
              selected={anc.seats?.[segmentKey(seatMapOpen.segIdx, seatMapOpen.paxIdx)]?.seat_number || null}
              onSelect={(seat, price) => {
                const k = segmentKey(seatMapOpen.segIdx, seatMapOpen.paxIdx);
                const next = { ...(anc.seats || {}) };
                if (!seat) delete next[k];
                else next[k] = { seat_number: seat, price: Number(price || 0), category: "Selected" };
                setAnc({ ...anc, seats: next });
                if (seat) setSeatMapOpen(null);
              }}
            />
          </DialogContent>
        )}
      </Dialog>

      <div className="flex items-center justify-between gap-3 pt-6">
        <Button type="button" variant="outline" onClick={() => navigate(-1)} className="rounded-full">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back
        </Button>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <div className="text-xs text-slate-500">Running total</div>
            <div className="font-heading text-xl font-bold text-[var(--brand-accent)]">{fmt(total)}</div>
          </div>
          <Button data-testid="anc-continue-btn" onClick={submit} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] rounded-full h-12 px-6">
            Review booking <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}
